import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, Observable, of, Subscription } from 'rxjs';

import { LoginApi } from '../../../api/login/login.api';
import { UsersApi } from '../../../api/users/users.api';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/* eslint-disable @typescript-eslint/naming-convention */
export enum Mode {
    SET,
    RESET,
    CHANGE
}

@Injectable()
export class PasswordResetService implements OnDestroy  {

    subscriptions: Subscription[] = [];

    urlParse: RegExp = /PasswordReset\/(.*)|ChangePassword|SetPassword/;

    private username$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private firstName$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private mode$: BehaviorSubject<Mode> = new BehaviorSubject<Mode>(null);
    private error$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private mode: Mode;

    token: string;
    wrongPass = false;

    readonly specialCharacterRegex = new RegExp(/([ !"#$%&'()*+,\-./\\:;<=>?@[\]^_`{|}~])/);

    constructor(
        public router: Router,
        public usersApi: UsersApi,
        public loginApi: LoginApi,
        public messageService: MessageService,
        public currentUserStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.router.events.subscribe((e) => {
                if (e instanceof NavigationEnd) {
                    this.initialRouteCheck(e.urlAfterRedirects);
                }
            })
        );
    }

    getUsername$(): Observable<string> {
        return this.username$;
    }
    getFirstName$(): Observable<string> {
        return this.firstName$;
    }
    getLoading$(): Observable<boolean> {
        return this.loading$;
    }
    getMode$(): Observable<Mode> {
        return this.mode$;
    }
    setMode$(mode: Mode): void {
        this.mode = mode;
        this.mode$.next(this.mode);
    }
    getError$(): Observable<string> {
        return this.error$;
    }
    setError$(error: string): void  {
        this.error$.next(error);
    }


    private initialRouteCheck(url: string) {
        const matches = url.match(this.urlParse) || [];

        if (!matches) {
            // Not a password reset
            return;
        } else {
            if (matches[1]) {
                this.setMode$(Mode.RESET);
                this.usersApi.getUserNameForToken(matches[1])
                    .pipe(
                        catchError((error) => {
                            this.loading$.next(false);
                            if (error?.status === 410) {
                                this.error$.next('PasswordReset.error.tokenExpired');
                            }
                            return of(null);
                        })
                    ).subscribe(username => {
                        if (username) {
                            this.token = matches[1];
                            this.username$.next(username);
                            this.loading$.next(false);
                        }
                    });
            } else {
                if (matches[0] === 'ChangePassword') {
                    this.setMode$(Mode.CHANGE);
                } else if (matches[0] === 'SetPassword') {
                    this.setMode$(Mode.SET);
                }
                this.setUserInfo();
            }
        }
    }

    setUserInfo() {
        this.subscriptions.push(
            this.currentUserStateService.getUser()
                .pipe(
                    catchError((error) => {
                        this.loading$.next(false);
                        this.error$.next('PasswordReset.generalError');
                        return of(null);
                    })
                )
                .subscribe(user => {
                    if (user) {
                        this.username$.next(user.username);
                        this.firstName$.next(user.firstName);
                        this.loading$.next(false);
                    }
                })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    setPassword(password: string) {
        this.loading$.next(true);
        this.usersApi.setPassword(this.token, password)
            .subscribe({
                next: () => {
                    this.loading$.next(false);
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary : 'PasswordReset.change.success'
                    });
                    this.error$.next(null);
                },
                error: () => {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary : 'PasswordReset.generalError'
                    });
                    this.error$.next('PasswordReset.error.unknown');
                    this.loading$.next(false);
                }
            });
    }

    changePassword(oldPass: string, newPass: string) {
        this.loading$.next(true);

        this.loginApi.changePassword(oldPass, newPass)
            .subscribe({
                next: () => {
                    this.loading$.next(false);
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary : 'PasswordReset.change.success'
                    });
                    this.error$.next(null);
                    if (this.mode === Mode.SET) {
                        this.router.navigate(['Dashboard']);
                    }
                },
                error: (error) => {
                    if ((error.status === 403) || (error.status === 401)) {
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary : 'PasswordReset.wrongPass'
                        });
                        this.error$.next('PasswordReset.error.wrongPass');
                    } else {
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary : 'PasswordReset.generalError'
                        });
                        this.error$.next('PasswordReset.error.unknown');
                    }
                    this.loading$.next(false);
                }
            });
    }

    passwordPatternValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        const password = control.value;

        if (password) {
            const errors = {
                special: password.match(this.specialCharacterRegex) === null,
                number: password.match(/[0-9]/) === null,
                uppercase: password.match(/[A-Z]/) === null,
                lowercase: password.match(/[a-z]/) === null
            };

            return Object.values(errors).some(e => !!e) ? errors : null;
        } else {
            return null;
        }
    };

    passwordsMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        const newPassword = control.get('newPassword')?.value;
        const confirmPassword = control.get('confirmPassword')?.value;

        return newPassword && confirmPassword && newPassword !== confirmPassword ? { passwordsMismatch: true } : null;
    };
}
